import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Ban, CalendarClock, Copy, Loader2, MailCheck, MailOpen, MailX,
  MessageCircle, MousePointerClick, Pause, Play, Search, Send, UserMinus, Users, CheckCheck,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames, formatDateTime } from '@/lib/utils';
import InfoTooltip from '@/components/ui/InfoTooltip';
import {
  IDIOMAS, NOMBRE_IDIOMA, canalDe, porcentaje, versionEmpezada,
  type Campana, type Envio, type Idioma,
} from '@/lib/comunicaciones';
import { VistaPreviaWhatsApp } from './PasoContenidoWhatsApp';
import { EstadoCampana } from '@/pages/comunicaciones/CampanasPage';
import VistaPrevia from './VistaPrevia';

/**
 * Una campaña que ya no es borrador: programada, saliendo o enviada. Cifras,
 * clics por enlace, cada destinatario con lo que ha hecho, y el contenido tal
 * cual salió. Mientras sale se refresca sola.
 */
export default function InformeCampana({ campana, recargar }: { campana: Campana; recargar: () => void }) {
  const navigate = useNavigate();
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const envios = useApi(() => (campana.lanzada ? api.getEnviosCampana(campana.id) : Promise.resolve([] as Envio[])), [campana.id, campana.estado, campana.resumen?.enviados]);
  const enlaces = useApi(() => (campana.lanzada ? api.getEnlacesCampana(campana.id) : Promise.resolve([])), [campana.id, campana.resumen?.clics]);

  // Mientras sale, refresco cada 15 s
  useEffect(() => {
    if (campana.estado !== 'enviando') return;
    const t = setInterval(recargar, 15_000);
    return () => clearInterval(t);
  }, [campana.estado, recargar]);

  const accion = async (nombre: string, fn: () => Promise<unknown>, confirmar?: string) => {
    if (confirmar && !window.confirm(confirmar)) return;
    setTrabajando(nombre);
    setError(null);
    try {
      await fn();
      recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo');
    } finally {
      setTrabajando(null);
    }
  };
  const duplicar = async () => {
    setTrabajando('duplicar');
    try {
      const c = await api.duplicarCampana(campana.id);
      navigate(`/comunicaciones/campanas/${c.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo duplicar');
      setTrabajando(null);
    }
  };

  const r = campana.resumen;
  const enviados = r?.enviados || 0;
  const whatsapp = canalDe(campana) === 'whatsapp';

  return (
    <div className="space-y-4">
      {/* Acciones */}
      <div className="flex flex-wrap items-center gap-2">
        <EstadoCampana c={campana} />
        <span className="text-sm text-sereno-dark/50">
          {campana.enviadaEn ? `Terminó de salir el ${formatDateTime(campana.enviadaEn)}`
            : campana.lanzada ? `Empezó a salir el ${formatDateTime(campana.lanzada.en)} · ${campana.lanzada.por}`
            : campana.programadaPara ? `Saldrá el ${formatDateTime(campana.programadaPara)}` : ''}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {campana.estado === 'programada' && (
            <Accion icono={CalendarClock} cargando={trabajando === 'desprogramar'} onClick={() => accion('desprogramar', () => api.desprogramarCampana(campana.id))}>
              Desprogramar y editar
            </Accion>
          )}
          {campana.estado === 'enviando' && (
            <Accion icono={Pause} cargando={trabajando === 'pausar'} onClick={() => accion('pausar', () => api.pausarCampana(campana.id))}>Pausar</Accion>
          )}
          {campana.estado === 'pausada' && (
            <Accion icono={Play} cargando={trabajando === 'reanudar'} onClick={() => accion('reanudar', () => api.reanudarCampana(campana.id))}>
              {campana.lanzada ? 'Reanudar' : 'Volver a borrador'}
            </Accion>
          )}
          {['programada', 'enviando', 'pausada'].includes(campana.estado) && (
            <Accion icono={Ban} peligro cargando={trabajando === 'cancelar'}
              onClick={() => accion('cancelar', () => api.cancelarCampana(campana.id), '¿Cancelar la campaña? Lo que ya salió no vuelve; lo pendiente no saldrá.')}>
              Cancelar
            </Accion>
          )}
          <Accion icono={Copy} cargando={trabajando === 'duplicar'} onClick={duplicar}>Duplicar</Accion>
        </div>
      </div>

      {error && <div className="card text-sm text-red-600">{error}</div>}
      {campana.detalle && (
        <div className="card border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {campana.detalle}
        </div>
      )}

      {r && (
        <>
          {campana.estado === 'enviando' && (
            <div className="card p-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-sereno-dark/60">Saliendo… se refresca sola</span>
                <span className="tabular-nums font-medium">{enviados + r.errores + r.omitidos} de {r.total}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-sereno-dark transition-all" style={{ width: `${r.total ? ((enviados + r.errores + r.omitidos) / r.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi icono={Users} etiqueta="Destinatarios" valor={String(r.total)} />
            <Kpi icono={Send} etiqueta="Enviados" valor={String(enviados)} pie={r.errores ? `${r.errores} con error` : r.omitidos ? `${r.omitidos} omitidos` : undefined} />
            <Kpi icono={MailCheck} etiqueta="Entregados" valor={porcentaje(r.entregados, enviados)} pie={r.noEntregados ? `${r.noEntregados} no entregados` : `${r.entregados}`} />
            {whatsapp ? <>
              <Kpi icono={CheckCheck} etiqueta="Leídos" valor={porcentaje(r.leidos || 0, enviados)} pie={`${r.leidos || 0} personas`}
                ayuda="El doble check azul. Quien tiene desactivada la confirmación de lectura no cuenta aunque lo lea." />
              <Kpi icono={MessageCircle} etiqueta="Respuestas" valor={String(r.respuestas || 0)} pie="personas que contestaron" />
            </> : <>
              <Kpi icono={MailOpen} etiqueta="Aperturas" valor={porcentaje(r.abiertos, enviados)} pie={`${r.abiertos} personas`}
                ayuda="Cuenta quien carga las imágenes o pincha un enlace. Apple Mail abre solo todos los correos, así que en iPhone sale de más; quien bloquea imágenes y no pincha, no sale." />
              <Kpi icono={MousePointerClick} etiqueta="Clics" valor={porcentaje(r.clics, enviados)} pie={`${r.clics} personas`} />
            </>}
            <Kpi icono={UserMinus} etiqueta="Bajas" valor={String(r.bajas)} pie={porcentaje(r.bajas, enviados)} />
          </div>
        </>
      )}

      {enlaces.data && enlaces.data.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sereno-dark text-sm">Clics por enlace</div>
          {/* En el móvil el enlace va en su línea y las cifras debajo: en una fila no se leería la dirección */}
          <ul className="divide-y divide-gray-100 text-sm">
            {enlaces.data.map(e => (
              <li key={`${e.idioma}-${e.n}`} className="px-4 py-2 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-0.5">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-6 shrink-0 text-xs text-sereno-dark/40 uppercase">{e.idioma}</span>
                  <a href={e.url} target="_blank" rel="noreferrer" title={e.url} className="truncate min-w-0 text-sereno-dark/80 hover:underline">{e.url.replace(/^https?:\/\//, '')}</a>
                </div>
                <div className="flex gap-4 pl-9 sm:pl-0 tabular-nums whitespace-nowrap">
                  <span>{e.personas} personas</span>
                  <span className="text-sereno-dark/40">{e.clics} clics</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {campana.lanzada && <Destinatarios envios={envios.data || []} cargando={envios.loading} whatsapp={whatsapp} />}

      {whatsapp ? <ContenidoWhatsApp campana={campana} /> : <Contenido campana={campana} />}
    </div>
  );
}

function Accion({ icono: Icono, onClick, cargando, peligro, children }: {
  icono: React.ElementType; onClick: () => void; cargando?: boolean; peligro?: boolean; children: ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} disabled={cargando}
      className={classNames('btn-ghost border text-sm flex items-center gap-1.5 px-3 py-1.5', peligro ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-gray-200')}>
      {cargando ? <Loader2 size={14} className="animate-spin" /> : <Icono size={14} />} {children}
    </button>
  );
}

function Kpi({ icono: Icono, etiqueta, valor, pie, ayuda }: { icono: React.ElementType; etiqueta: string; valor: string; pie?: string; ayuda?: string }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-1.5 text-xs text-sereno-dark/40"><Icono size={14} /> {etiqueta} {ayuda && <InfoTooltip text={ayuda} />}</div>
      <div className="text-2xl font-bold text-sereno-dark tabular-nums mt-1">{valor}</div>
      {pie && <div className="text-xs text-sereno-dark/40 mt-0.5">{pie}</div>}
    </div>
  );
}

type Filtro = 'todos' | 'abrieron' | 'clicaron' | 'leyeron' | 'contestaron' | 'noEntregados' | 'bajas' | 'errores';
const FILTROS: { id: Filtro; texto: string; cumple: (e: Envio) => boolean; canal?: 'email' | 'whatsapp' }[] = [
  { id: 'todos', texto: 'Todos', cumple: () => true },
  { id: 'abrieron', texto: 'Abrieron', cumple: e => !!e.abierto, canal: 'email' },
  { id: 'clicaron', texto: 'Pincharon', cumple: e => !!e.clics, canal: 'email' },
  { id: 'leyeron', texto: 'Leyeron', cumple: e => !!e.leido, canal: 'whatsapp' },
  { id: 'contestaron', texto: 'Contestaron', cumple: e => (e.respuestas || 0) > 0, canal: 'whatsapp' },
  { id: 'noEntregados', texto: 'No entregados', cumple: e => e.entrega?.ok === false },
  { id: 'bajas', texto: 'Bajas', cumple: e => !!e.baja },
  { id: 'errores', texto: 'Errores y omitidos', cumple: e => ['error', 'interrumpido', 'omitido'].includes(e.estado) },
];

const ESTADO_ENVIO: Record<Envio['estado'], string> = {
  pendiente: 'En cola', enviando: 'Saliendo', enviado: 'Enviado', error: 'Error', omitido: 'Omitido', interrumpido: 'Cortado',
};

function Destinatarios({ envios, cargando, whatsapp }: { envios: Envio[]; cargando: boolean; whatsapp: boolean }) {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(50);
  const filas = useMemo(() => {
    const f = FILTROS.find(x => x.id === filtro)!;
    const q = busca.trim().toLowerCase();
    return envios.filter(e => f.cumple(e) && (!q || e.email.includes(q) || (e.telefono || '').includes(q) || e.nombre.toLowerCase().includes(q)));
  }, [envios, filtro, busca]);

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <span className="font-semibold text-sereno-dark text-sm mr-2">Destinatarios {cargando && <Loader2 size={13} className="inline animate-spin" />}</span>
        {FILTROS.filter(f => !f.canal || f.canal === (whatsapp ? 'whatsapp' : 'email')).map(f => {
          const n = envios.filter(f.cumple).length;
          return (
            <button key={f.id} type="button" onClick={() => setFiltro(f.id)}
              className={classNames('text-xs px-2.5 py-1 rounded-full border', filtro === f.id ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
              {f.texto} <span className="opacity-60">{n}</span>
            </button>
          );
        })}
        <div className="relative w-full sm:w-auto sm:ml-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
          <input className="input py-1 pl-8 text-sm w-full sm:w-48" placeholder="Buscar" value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      </div>
      {/* Scroll propio solo en pantallas grandes: en el móvil un scroll dentro de otro atrapa el dedo */}
      <div className="overflow-x-auto sm:max-h-[480px] sm:overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-left text-xs uppercase tracking-wide text-sereno-dark/40 border-b border-gray-100">
              <th className="px-4 py-2 font-medium">Destinatario</th>
              <th className="px-4 py-2 font-medium hidden md:table-cell">Tipo</th>
              <th className="px-4 py-2 font-medium">Envío</th>
              <th className="px-4 py-2 font-medium hidden sm:table-cell">Entrega</th>
              {whatsapp ? <>
                <th className="px-4 py-2 font-medium text-center" title="Leído"><CheckCheck size={14} className="inline" /></th>
                <th className="px-4 py-2 font-medium text-center" title="Respuestas"><MessageCircle size={14} className="inline" /></th>
              </> : <>
                <th className="px-4 py-2 font-medium text-center" title="Aperturas"><MailOpen size={14} className="inline" /></th>
                <th className="px-4 py-2 font-medium text-center" title="Clics"><MousePointerClick size={14} className="inline" /></th>
              </>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filas.slice(0, limite).map(e => (
              <tr key={e.id}>
                <td className="px-4 py-2">
                  <div className="text-sereno-dark">{e.nombre || <span className="text-sereno-dark/40">Sin nombre</span>}</div>
                  <div className="text-xs text-sereno-dark/40 break-all">{whatsapp ? e.telefono : e.email} · {(e.idiomaEnviado || e.idioma).toUpperCase()}</div>
                </td>
                <td className="px-4 py-2 hidden md:table-cell text-sereno-dark/60 capitalize">{e.tipo}</td>
                <td className="px-4 py-2">
                  <span className={classNames('text-xs', ['error', 'interrumpido'].includes(e.estado) ? 'text-red-600' : e.estado === 'omitido' ? 'text-sereno-dark/40' : 'text-sereno-dark/70')} title={e.detalle}>
                    {ESTADO_ENVIO[e.estado]}
                  </span>
                  {e.baja && <span className="badge bg-red-100 text-red-700 ml-1.5">Baja</span>}
                </td>
                <td className="px-4 py-2 hidden sm:table-cell text-xs">
                  {e.entrega ? (
                    <span className={e.entrega.ok ? 'text-emerald-700' : 'text-red-600'} title={e.entrega.detalle}>
                      {e.entrega.ok
                        ? 'Entregado'
                        : <span className="inline-flex items-center gap-1"><MailX size={12} /> {whatsapp ? 'No entregado' : e.entrega.estado}</span>}
                    </span>
                  ) : <span className="text-sereno-dark/30">—</span>}
                </td>
                {whatsapp ? <>
                  <td className="px-4 py-2 text-center text-xs" title={e.leido ? `Leído: ${formatDateTime(e.leido)}` : undefined}>{e.leido ? <CheckCheck size={14} className="inline text-sky-600" /> : ''}</td>
                  <td className="px-4 py-2 text-center tabular-nums text-xs">{e.respuestas || ''}</td>
                </> : <>
                  <td className="px-4 py-2 text-center tabular-nums text-xs" title={e.abierto ? `Primera: ${formatDateTime(e.abierto.primera)}` : undefined}>{e.abierto?.veces || ''}</td>
                  <td className="px-4 py-2 text-center tabular-nums text-xs">{e.clics?.veces || ''}</td>
                </>}
              </tr>
            ))}
            {filas.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-sereno-dark/40">Nadie en este filtro</td></tr>
            )}
          </tbody>
        </table>
        {filas.length > limite && (
          <button type="button" onClick={() => setLimite(l => l + 100)} className="w-full py-2.5 text-sm text-sereno-dark/60 hover:text-sereno-dark border-t border-gray-100">
            Ver más ({filas.length - limite} sin mostrar)
          </button>
        )}
      </div>
    </div>
  );
}

function ContenidoWhatsApp({ campana }: { campana: Campana }) {
  const plantillas = useApi(() => api.getPlantillasWhatsApp(), []);
  const p = plantillas.data?.find(x => x.nombre === campana.whatsapp?.plantilla);
  return (
    <div className="card space-y-3">
      <div className="font-semibold text-sereno-dark text-sm">Contenido · plantilla <span className="font-mono text-xs">{campana.whatsapp?.plantilla}</span></div>
      {plantillas.error && <div className="text-sm text-red-600">{plantillas.error}</div>}
      {p ? <div className="max-w-md"><VistaPreviaWhatsApp campana={campana} plantilla={p} /></div>
        : plantillas.data && <p className="text-sm text-sereno-dark/40">La plantilla ya no está en la cuenta de Meta.</p>}
    </div>
  );
}

function Contenido({ campana }: { campana: Campana }) {
  const idiomas = IDIOMAS.filter(i => versionEmpezada(campana.versiones[i]));
  const [idioma, setIdioma] = useState<Idioma>(idiomas.includes(campana.idiomaReserva) ? campana.idiomaReserva : idiomas[0] || 'es');
  const version = campana.versiones[idioma];
  if (!version) return null;
  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-sereno-dark text-sm mr-2">Contenido</span>
        {idiomas.map(i => (
          <button key={i} type="button" onClick={() => setIdioma(i)}
            className={classNames('text-xs px-2.5 py-1 rounded-full border', i === idioma ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
            {NOMBRE_IDIOMA[i]}
          </button>
        ))}
        <span className="ml-auto text-xs text-sereno-dark/40">Desde {campana.remitente.direccion}</span>
      </div>
      <div className="max-w-2xl">
        <VistaPrevia version={version} idioma={idioma} tipo={campana.audiencia.suscriptores.incluir ? 'suscriptor' : 'propietario'} remitente={campana.remitente.direccion} />
      </div>
    </div>
  );
}
